import ky, { type InitHook } from "ky";
import {
  AUTH_API_URL,
  getLogisticsApiUrl,
  MAPBOX_API_URL,
  MAPBOX_PUBLIC_ACCESS_TOKEN,
  getNotificationApiUrl,
  getPatientApiUrl,
  userManager,
  getAcrValues,
} from "../Utils/EnvUtils";
import { User } from "oidc-client-ts";
import { scopes as idsScopes } from "@/Utils/EnvUtils";
import { injectSelectedSites } from "@medoma/shared-employee/selectedSites";

const signinRedirect = () => {
  return userManager.signinRedirect({
    acr_values: getAcrValues(),
    scope: idsScopes,
    state: window.location.pathname,
  });
};

const getAuthenticatedUser = () => {
  const oidcStorage = sessionStorage.getItem(
    `oidc.user:${AUTH_API_URL}:medoma-center`,
  );
  if (!oidcStorage) {
    void signinRedirect();
    throw new Error(
      "Missing session storage entry. Redirecting to signin to amend this.",
    );
  }

  const user = User.fromStorageString(oidcStorage);
  if (user.expired) {
    void signinRedirect();
    throw new Error(
      "The user's credentials have expired. Redirecting to signin to amend this.",
    );
  }

  return user;
};

const kyBase = ky.create({
  retry: { limit: 0 }, // axios-like
  timeout: false, // axios-like
});

// Resolve the organization-specific base URL and attach auth in an `init` hook (not
// `beforeRequest`). `init` mutates the options before ky builds the Request, so
// we set `prefix` instead of reconstructing the Request via `new Request(url,
// request)` — the latter drops the body of POST/PATCH/PUT requests in Firefox
// (the body is a ReadableStream that doesn't survive reconstruction).
const authedInitHook =
  (buildBaseUrl: (organization: string) => string): InitHook =>
  (options) => {
    // Test: no session, no token; resolve against the "medoma" organization.
    if (import.meta.env.MODE === "test") {
      options.prefix = buildBaseUrl("medoma");
      return;
    }

    // getAuthenticatedUser() throws + redirects if there's no live session,
    // so we never build a tokenless request in prod.
    const user = getAuthenticatedUser();
    // The auth service names the organization claim `unit`.
    options.prefix = buildBaseUrl(user.profile.unit as string);

    const headers = new Headers(options.headers as HeadersInit | undefined);
    headers.set("Authorization", `Bearer ${user.access_token}`);
    injectSelectedSites(headers, { userId: user.profile.sub });
    options.headers = headers;
  };

export const logisticsApi = kyBase.extend({
  hooks: {
    init: [authedInitHook(getLogisticsApiUrl)],
  },
});

export const patientApi = kyBase.extend({
  hooks: {
    init: [authedInitHook(getPatientApiUrl)],
  },
});

export const notificationApi = kyBase.extend({
  hooks: {
    init: [authedInitHook(getNotificationApiUrl)],
  },
});

export const mapboxApi = kyBase.extend({
  prefix: MAPBOX_API_URL,
  hooks: {
    beforeRequest: [
      ({ request }) => {
        // Don't append an access token in test scenario.
        if (import.meta.env.MODE === "test") {
          return;
        }
        const url = new URL(request.url);
        url.searchParams.set("access_token", MAPBOX_PUBLIC_ACCESS_TOKEN);
        return new Request(url, request);
      },
    ],
  },
});
